import { useMemo } from 'react';
import { COUNTRIES, findCountry, purchaseCurrencyForCountry } from '@bbpos/types';
import { resolveSupplierTaxId } from '@bbpos/ui';
import type { SupplierTaxIdPresentation } from '@bbpos/ui';
import { useTenantCountry } from '@/hooks/use-tenant-country';
import { useTenantCurrency } from '@/hooks/use-tenant-currency';
import { addressFieldsFor } from '@/utils/address-fields';
import type { AddressFields } from '@/utils/address-fields';
import { STATE_OPTIONS } from './constants';

/** Options for a supplier's Country select: every country the apps know, flag first. */
export const SUPPLIER_COUNTRY_OPTIONS = COUNTRIES.map((c) => ({
  value: c.iso,
  label: `${c.flag} ${c.name}`,
}));

export interface SupplierCountry {
  iso: string;
  name: string;
  dial: string;
  /** Null where the country has no supplier tax id (Kuwait, Qatar): hide the field. */
  taxId: SupplierTaxIdPresentation | null;
  addressFields: AddressFields;
  /** Registered outside the tenant's own country. */
  isForeign: boolean;
  /**
   * The currency a supplier there usually invoices in, else the tenant's: the
   * default for the supplier's Currency (multi-currency-purchasing-plan D6).
   */
  suggestedCurrency: string;
}

/**
 * The currency a supplier registered in `iso` usually invoices in, else the
 * tenant's (multi-currency-purchasing-plan D6). Pure, so a form can work out
 * the suggestion for a country it is switching to in the same event.
 */
export function suggestedSupplierCurrency(iso: string, tenantIso: string, tenantCurrency: string): string {
  return iso === tenantIso ? tenantCurrency : purchaseCurrencyForCountry(iso) ?? tenantCurrency;
}

/**
 * Everything a supplier form needs from the country the supplier is
 * registered in (client issue I-3): its tax id rule, address fields and phone
 * dial code. A supplier with no country is in the tenant's own country.
 */
export function useSupplierCountry(iso: string | null | undefined): SupplierCountry {
  const tenant = useTenantCountry();
  const tenantCurrency = useTenantCurrency();
  return useMemo(() => {
    const code = iso || tenant.iso;
    const country = findCountry(code);
    return {
      iso: code,
      name: country?.name ?? code,
      dial: country?.dial ?? tenant.dial,
      taxId: resolveSupplierTaxId(code),
      addressFields: addressFieldsFor({ iso: code }, STATE_OPTIONS),
      isForeign: code !== tenant.iso,
      suggestedCurrency: suggestedSupplierCurrency(code, tenant.iso, tenantCurrency),
    };
  }, [iso, tenant, tenantCurrency]);
}
